import React, { useState, useMemo } from 'react';
import { UsersIcon, UserGroupIcon, CalendarIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { UsersIcon as UsersIconSolid } from '@heroicons/react/24/solid';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

interface RegisteredMembersSectionProps {
  totalMembers: number;
  ageGroupData: Array<{ name: string; value: number }>;
  genderData: Array<{ name: string; value: number }>;
  memberClassData: Array<{ name: string; value: number }>;
}

const GENDER_COLORS = ['#0088FE', '#00C49F', '#FFBB28'];
const CLASS_COLORS = ['#FF8042', '#FFBB28', '#00C49F'];
const AGE_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe', '#00c49f'];

export const RegisteredMembersSection: React.FC<RegisteredMembersSectionProps> = ({
  totalMembers,
  ageGroupData,
  genderData,
  memberClassData
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'demographics' | 'classes' | 'trends'>('overview');
  
  // 차트 데이터 최적화
  const optimizedGenderData = useMemo(() => {
    return genderData.map(item => ({
      ...item,
      percentage: totalMembers > 0 ? ((item.value / totalMembers) * 100).toFixed(1) : '0'
    }));
  }, [genderData, totalMembers]);

  const optimizedAgeData = useMemo(() => {
    return ageGroupData.map(item => ({
      ...item,
      percentage: totalMembers > 0 ? ((item.value / totalMembers) * 100).toFixed(1) : '0'
    }));
  }, [ageGroupData, totalMembers]);


  // 레이더 차트 데이터 생성
  const radarData = useMemo(() => {
    if (totalMembers === 0) return [];
    
    return [
      {
        subject: '남성',
        A: genderData.find(g => g.name === '남성')?.value || 0,
        fullMark: totalMembers,
        percentage: genderData.find(g => g.name === '남성') ? parseFloat(optimizedGenderData.find(g => g.name === '남성')?.percentage || '0') : 0
      },
      {
        subject: '여성',
        A: genderData.find(g => g.name === '여성')?.value || 0,
        fullMark: totalMembers,
        percentage: genderData.find(g => g.name === '여성') ? parseFloat(optimizedGenderData.find(g => g.name === '여성')?.percentage || '0') : 0
      },
      {
        subject: '20-30대',
        A: ageGroupData.find(a => a.name === '20대')?.value || 0,
        fullMark: totalMembers,
        percentage: ageGroupData.find(a => a.name === '20대') ? parseFloat(optimizedAgeData.find(a => a.name === '20대')?.percentage || '0') : 0
      },
      {
        subject: '30-40대',
        A: ageGroupData.find(a => a.name === '30대')?.value || 0,
        fullMark: totalMembers,
        percentage: ageGroupData.find(a => a.name === '30대') ? parseFloat(optimizedAgeData.find(a => a.name === '30대')?.percentage || '0') : 0
      },
      {
        subject: '40-50대',
        A: ageGroupData.find(a => a.name === '40대')?.value || 0,
        fullMark: totalMembers,
        percentage: ageGroupData.find(a => a.name === '40대') ? parseFloat(optimizedAgeData.find(a => a.name === '40대')?.percentage || '0') : 0
      }
    ];
  }, [genderData, ageGroupData, totalMembers, optimizedGenderData, optimizedAgeData]);

  // 막대 차트 데이터 생성
  const barChartData = useMemo(() => {
    const ageGroups = ['10대 이하', '20대', '30대', '40대', '50대', '60대 이상'];
    return ageGroups.map(group => ({
      name: group,
      회원수: ageGroupData.find(item => item.name === group)?.value || 0,
      비율: totalMembers > 0 ? ((ageGroupData.find(item => item.name === group)?.value || 0) / totalMembers * 100).toFixed(1) : '0'
    }));
  }, [ageGroupData, totalMembers]);

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 mb-8 shadow-lg border border-indigo-200 dark:border-indigo-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl">
            <UsersIconSolid className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">등록 교인 현황</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">교회 성도들의 다양한 특성 분석</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 rounded-full text-sm font-medium">
            <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2 inline-block"></span>
            총 {totalMembers.toLocaleString()}명
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="flex flex-wrap gap-1 mb-6 bg-white dark:bg-gray-800 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'overview'
              ? 'bg-indigo-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          📊 전체 현황
        </button>
        <button
          onClick={() => setActiveTab('demographics')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'demographics'
              ? 'bg-indigo-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          👥 성도종합
        </button>
        <button
          onClick={() => setActiveTab('classes')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'classes'
              ? 'bg-indigo-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          📋 등급 분포
        </button>
        <button
          onClick={() => setActiveTab('trends')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'trends'
              ? 'bg-indigo-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          📈 트렌드 분석
        </button>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 통계 카드 */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">총 등록 교인</p>
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{totalMembers.toLocaleString()}명</p>
              </div>
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <UsersIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">평균 연령</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">42.5세</p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <CalendarIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>
        </div>

        {/* 주요 내용 */}
        <div className="lg:col-span-3">
          {activeTab === 'overview' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">교회 현황</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">성별 분포</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={genderData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} labelLine={false} label={{ fontSize: 12 }}>
                        {genderData.map((_entry, index) => (
                          <Cell key={`cell-gender-${index}`} fill={GENDER_COLORS[index % GENDER_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">연령대 분포</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={ageGroupData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} labelLine={false} label={{ fontSize: 12 }}>
                        {ageGroupData.map((_entry, index) => (
                          <Cell key={`cell-age-${index}`} fill={AGE_COLORS[index % AGE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'demographics' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">성도종합 상세</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">성별 비율</h4>
                  <div className="space-y-3">
                    {genderData.map((item, index) => (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: GENDER_COLORS[index % GENDER_COLORS.length] }}></div>
                          <span className="text-sm text-gray-600 dark:text-gray-300">{item.name}</span>
                        </div>
                        <span className="font-medium text-gray-800 dark:text-white">{item.value}명 ({((item.value / totalMembers) * 100).toFixed(1)}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">연령대 비율</h4>
                  <div className="space-y-3">
                    {ageGroupData.map((item, index) => (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: AGE_COLORS[index % AGE_COLORS.length] }}></div>
                          <span className="text-sm text-gray-600 dark:text-gray-300">{item.name}</span>
                        </div>
                        <span className="font-medium text-gray-800 dark:text-white">{item.value}명 ({((item.value / totalMembers) * 100).toFixed(1)}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'classes' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">등급 분포</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">등급별 분포</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={memberClassData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} labelLine={false} label={{ fontSize: 12 }}>
                        {memberClassData.map((_entry, index) => (
                          <Cell key={`cell-class-${index}`} fill={CLASS_COLORS[index % CLASS_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">등급별 상세</h4>
                  <div className="space-y-3">
                    {memberClassData.map((item, index) => (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CLASS_COLORS[index % CLASS_COLORS.length] }}></div>
                          <span className="text-sm text-gray-600 dark:text-gray-300">{item.name}</span>
                        </div>
                        <span className="font-medium text-gray-800 dark:text-white">{item.value}명 ({((item.value / totalMembers) * 100).toFixed(1)}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-6">📈 고급 트렌드 분석</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* 레이더 차트 */}
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">다차원 성도 분석</h4>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="subject" />
                        <PolarRadiusAxis angle={90} domain={[0, 'dataMax + 50']} />
                        <Radar
                          name="회원 수"
                          dataKey="A"
                          stroke="#8884d8"
                          fill="#8884d8"
                          fillOpacity={0.3}
                          strokeWidth={2}
                        />
                        <Tooltip
                          formatter={(value, name) => [
                            `${value}명 (${radarData.find(d => d.A === value)?.percentage || 0}%)`,
                            name
                          ]}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* 막대 차트 */}
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">연령대별 분포 (막대 차트)</h4>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={barChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip
                          formatter={(value, name) => [
                            name === '회원수' ? `${value}명` : `${value}%`,
                            name === '회원수' ? '회원 수' : '비율'
                          ]}
                        />
                        <Bar dataKey="회원수" fill="#82ca9d" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* 통계 요약 */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">가장 많은 연령대</p>
                      <p className="text-xl font-bold text-blue-800 dark:text-blue-200">
                        {ageGroupData.reduce((max, item) => (item.value > max.value ? item : max), ageGroupData[0] || { name: 'N/A', value: 0 }).name}
                      </p>
                    </div>
                    <ChartBarIcon className="h-8 w-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 dark:text-green-400 font-medium">성별 비율</p>
                      <p className="text-xl font-bold text-green-800 dark:text-green-200">
                        {genderData.length >= 2 ?
                          `${genderData[0].value}:${genderData[1].value}` :
                          'N/A'}
                      </p>
                    </div>
                    <UserGroupIcon className="h-8 w-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">등급 다양성</p>
                      <p className="text-xl font-bold text-purple-800 dark:text-purple-200">
                        {memberClassData.length}개 등급
                      </p>
                    </div>
                    <UsersIcon className="h-8 w-8 text-purple-500" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};